import jQuery from 'jquery';
import AhaDrawer from 'javascripts/drawer';

const SUPPORTED_REPORT_PATHS = [
  'custom_pivots',
  'cycle_time_charts',
  'iteration_burnups',
  'gantt_roadmaps',
  'epic_by_release_gantt_roadmaps',
  'epic_gantt_roadmaps',
  'initiative_gantt_roadmaps',
  'develop_timelines',
  'heatmap_charts',
];
const REPORT_REGEX = SUPPORTED_REPORT_PATHS.join('|');

export function getReportReferenceFromUrl(url: string | undefined) {
  if (!url) {
    return null;
  }

  let urlParts = url.split('/');
  // Remove any blank parts from the URL
  urlParts = urlParts.filter(part => part.length > 0).reverse();

  const regex = new RegExp(`/bookmarks/(?:${REPORT_REGEX})/\\d+(?:/\\d+)?`);

  // Get the last id from the URL if it matches the pattern
  if (url.match(regex)) {
    // This is a report URL, extract
    const lastPart = urlParts.find(part => part.match(/(\d+)/));
    const match = lastPart?.match(/(\d+)/)?.[0];

    if (match) {
      return `Report-${match}`;
    }
  }

  return null;
}

export function getArtifactReferenceFromUrl(url: string | undefined) {
  if (!url) {
    return null;
  }

  // https://reallybigaha.ahalocalhost.com:3000/assistant/shared_artifacts/AT6G4PxoksBTD6XNocP5vj7EBQNGuGOUNOBmnibFXnje/1
  // Artifact-AT6G4PxoksBTD6XNocP5vj7EBQNGuGOUNOBmnibFXnje-1
  // or Artifact-AT6G4PxoksBTD6XNocP5vj7EBQNGuGOUNOBmnibFXnje when no version
  const artifactMatch = url.match(
    /\/assistant\/shared_artifacts\/([a-zA-Z0-9]+)(?:\/(\d+))?/
  );
  if (artifactMatch) {
    const artifactId = artifactMatch[1];
    const version = artifactMatch[2];
    return version
      ? `Artifact-${artifactId}-${version}`
      : `Artifact-${artifactId}`;
  }

  return null;
}

export function getReferenceNumFromUrl(url: string | undefined) {
  if (!url) {
    return null;
  }
  let urlParts = url.split('/');
  // Remove any blank parts from the URL
  urlParts = urlParts.filter(part => part.length > 0);
  const foundRef = urlParts
    .reverse()
    .find(part => part.match(/[a-zA-Z0-9]+-\d+/));

  if (foundRef) {
    // strip any query params and # fragments
    const cleanRef = foundRef.split(/[?#.]/)[0];
    return cleanRef;
  }

  const reportRef = getReportReferenceFromUrl(url);
  if (reportRef) {
    return reportRef;
  }

  return null;
}

export function getReferenceNumsOfSelectedRecords() {
  return Array.from(document.querySelectorAll('aha-card[selected]'))
    .map(el => {
      const url =
        el.getAttribute('data-drawer-url') ||
        el.getAttribute('data-record-ref-num');
      if (url) {
        return getReferenceNumFromUrl(url);
      }
      return null;
    })
    .filter(Boolean);
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class AssistantContext {
  static #instance: AssistantContext;
  private constructor() {}
  public static get instance(): AssistantContext {
    if (!AssistantContext.#instance) {
      AssistantContext.#instance = new AssistantContext();
    }

    return AssistantContext.#instance;
  }

  context: Record<string, any> = {};

  // Some places in the app don't open drawers with ref nums in the URL, so we
  // need to wait for the drawer to load and then pull the ref num from there.
  // We only do this as a fallback if we don't have a ref num from the URL.
  waitForDrawerLoad = false;

  pushAssistantContextChanged() {
    const event = new CustomEvent('aha-assistant:context-changed', {
      detail: this.context,
    });

    window.dispatchEvent(event);
  }

  setStickyReferencesFromExternal(references: string[]) {
    references.forEach((ref: string) => {
      const manualRef =
        getReportReferenceFromUrl(ref) ||
        getArtifactReferenceFromUrl(ref) ||
        getReferenceNumFromUrl(ref);
      if (!manualRef) return false;

      AssistantContext.instance.addManualReference(manualRef);
    });
    this.pushAssistantContextChanged();
  }

  setStickyReferences(references: string[]) {
    // Add references to context without removing existing references
    // Replace all references with the new array
    this.context.stickyReferences = references;
    this.pushAssistantContextChanged();
  }

  addManualReference(reference: string) {
    if (!this.context.stickyReferences) {
      this.context.stickyReferences = [];
    }
    if (!this.context.stickyReferences.includes(reference)) {
      this.setStickyReferences([...this.context.stickyReferences, reference]);
    }
  }

  setReferences(references: string[]) {
    // Replace all references with the new array
    this.context.references = references;
    this.pushAssistantContextChanged();
  }

  removeReferenceFromContext(reference?: string) {
    if (
      reference &&
      (this.context.references || this.context.stickyReferences)
    ) {
      // Remove specific reference from array
      this.context.references = this.context.references.filter(
        (ref: string) => ref !== reference
      );
      this.context.stickyReferences = this.context.stickyReferences.filter(
        (ref: string) => ref !== reference
      );
    } else {
      this.context.references = [];
      this.context.stickyReferences = [];
    }

    this.pushAssistantContextChanged();
  }
}

function handleSelectionChanged() {
  if (AhaDrawer?.isOpen?.()) {
    return;
  }

  addReferencesFromPage();
}

function handleLocationChange() {
  AssistantContext.instance.setStickyReferences([]);
  AssistantContext.instance.setReferences([]);

  addReferencesFromPage();
}

function addReferencesFromPage() {
  const referenceNum = getReferenceNumFromUrl(window.location.pathname);
  const referenceNums = getReferenceNumsOfSelectedRecords();
  if (referenceNum && !referenceNums.includes(referenceNum)) {
    referenceNums.push(referenceNum);
  }
  AssistantContext.instance.setReferences(referenceNums);
}

function handleDrawerLoad(event: any) {
  if (!AssistantContext.instance.waitForDrawerLoad) {
    return;
  }
  AssistantContext.instance.waitForDrawerLoad = false;

  const drawer = document.querySelectorAll(
    event.detail.drawer.uniqueDrawerSelector
  )?.[0];
  if (!drawer) {
    return;
  }

  const refPill = drawer.querySelectorAll(
    ':scope #drawer-nav__row--actions .record-reference-pill'
  )?.[0];

  if (refPill) {
    // If we have a reference pill, use that to set the context
    const referenceNumUrl = refPill.getAttribute('data-clipboard-text');
    if (referenceNumUrl) {
      const referenceNum = getReferenceNumFromUrl(referenceNumUrl);
      if (referenceNum) {
        AssistantContext.instance.setReferences([referenceNum]);
      }
    }
  }
}

// Listen for page changes (Turbolinks navigation)
jQuery(document).on('page:change', handleLocationChange);

// Listen for browser back/forward navigation
window.addEventListener('popstate', handleLocationChange);
window.addEventListener('aha-record:selection:changed', handleSelectionChanged);

// Listen for custom events that may change the location
document.addEventListener('aha.redirect_to', function () {
  // NOTE: this event is dispatched before the url changes, so we need to wait a tick
  setTimeout(handleLocationChange, 0);
});

document.addEventListener('drawer:load', handleDrawerLoad);

export default AssistantContext;
